import {sendAnalyticsEvent} from '#/analytics/analytics';
import LandIcon from '#/components/icons/land-icon';
import pin from '#/components/icons/pin.svg';
import environment from '#/environment/environment';
import {useLands} from '#/hooks/query/use-lands';
import {useUnlistedLands} from '#/hooks/query/use-unlisted-lands';
import {useQueryNavigationActions} from '#/hooks/use-query-navigation-actions';
import {useSelectedLand} from '#/hooks/use-selected-land';
import {landHasMarket} from '#/utils/land-utils';
import {distanceInMiles} from '#/utils/math-utils';
import {getNumberPositivityStatus} from '#/utils/number-utils';
import 'mapbox-gl/dist/mapbox-gl.css';
import {FunctionComponent, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Map, {
  Layer,
  MapLayerMouseEvent,
  MapRef,
  Marker,
  NavigationControl,
  Source,
  ViewStateChangeEvent,
} from 'react-map-gl';
import {usePrevious} from 'react-use';
import {Land, UnlistedLand} from 'shareland-common';

const MIN_ZOOM = 4.5;
const MAX_ZOOM = 20;
const MIN_PITCH = 0;
const MAX_PITCH = 45;

const calculatePitchFromZoom = (zoom: number) => {
  const normalizedZoom = zoom - MIN_ZOOM;
  const zoomRange = MAX_ZOOM - MIN_ZOOM;

  const pitchPercentage = normalizedZoom / zoomRange;

  const pitchRange = MAX_PITCH - MIN_PITCH;

  return pitchRange * pitchPercentage;
};

const isVisibleForZoom = (land: Land | UnlistedLand, zoomLevel: number, isAutomaticTransition: boolean): boolean => {
  if ((land as Land).syntheticMarketData) {
    return true;
  } else if (isAutomaticTransition) {
    return false;
  }
  if (land.type === 'NEIGHBORHOOD') {
    return zoomLevel > 10;
  } else {
    return zoomLevel > 8;
  }
};

const mapLayerFilterForLand = (land: UnlistedLand) => ['==', 'ID', land.externalDemographicsId];

const LandMap: FunctionComponent<{
  mapCenterOffsetX?: number;
  animate?: boolean;
  showNavigation?: boolean;
  displayOnlySelectedLand?: boolean;
}> = ({mapCenterOffsetX, animate = true, showNavigation = true, displayOnlySelectedLand = false}) => {
  const {t} = useTranslation('translation');
  const {data: lands} = useLands();
  const {data: unlistedLands} = useUnlistedLands();

  const mapRef = useRef<MapRef>(null);
  const {data: selectedLand} = useSelectedLand();

  const {
    actionMenu: {openInformation, close},
  } = useQueryNavigationActions();

  const [zoomLevelForLandVisibility, setZoomLevelForLandVisibility] = useState<number>(() => 12);

  const previousZoomLevelForLandVisibility = usePrevious(zoomLevelForLandVisibility);

  const [isAutomaticTransition, setIsAutomaticTransition] = useState(false);

  const visibleLands = useMemo(() => {
    return (
      lands?.filter(land => {
        if (displayOnlySelectedLand && selectedLand?.id !== land.id) return false;
        return isVisibleForZoom(land, zoomLevelForLandVisibility, isAutomaticTransition);
      }) ?? []
    );
  }, [isAutomaticTransition, lands, selectedLand?.id, displayOnlySelectedLand, zoomLevelForLandVisibility]);

  const [state, setState] = useState({
    latitude: selectedLand?.mapInformation.center.lat ?? 34.0825477,
    longitude: selectedLand?.mapInformation.center.lng ?? -118.4170435,
    zoom: 8,
    pitch: calculatePitchFromZoom(8),
  });

  const visibleUnlistedLands = useMemo(() => {
    if (displayOnlySelectedLand) return [];

    return (
      unlistedLands?.filter(land => {
        return (
          selectedLand?.id === land.id ||
          (isVisibleForZoom(land, zoomLevelForLandVisibility, isAutomaticTransition) &&
            distanceInMiles(
              state.latitude,
              state.longitude,
              land.mapInformation.center.lat,
              land.mapInformation.center.lng,
            ) < 100)
        );
      }) ?? []
    );
  }, [
    displayOnlySelectedLand,
    isAutomaticTransition,
    selectedLand?.id,
    state.latitude,
    state.longitude,
    unlistedLands,
    zoomLevelForLandVisibility,
  ]);

  const onOpenLand = (land: Land) => {
    sendAnalyticsEvent.mapViewProperty({
      land,
    });

    openInformation(land);
  };

  const onMapClick = (event: MapLayerMouseEvent) => {
    const selectedIds = event.features?.map(feature => feature.properties?.ID ?? '') ?? [];
    const matchingLands = visibleLands.filter(land => {
      return selectedIds.includes(land.externalDemographicsId);
    });
    const land = matchingLands[0];

    if (land) {
      onOpenLand(land);
    } else {
      const matchingLands = visibleUnlistedLands.filter(land => {
        return selectedIds.includes(land.externalDemographicsId);
      });
      const land = matchingLands[0];
      if (land) {
        openInformation(land);
      }
    }
  };

  const mapSelectedLandFilter = useMemo(() => {
    const noFilter = ['==', 'ID', ''];

    if (!selectedLand) return noFilter;

    return ['any', mapLayerFilterForLand(selectedLand)];
  }, [selectedLand]);

  const landFilter = useMemo(() => {
    return ['any', ...visibleLands.map(mapLayerFilterForLand)];
  }, [visibleLands]);

  const onViewStateChange = (event: ViewStateChangeEvent) => {
    setState({
      latitude: event.viewState.latitude,
      longitude: event.viewState.longitude,
      zoom: event.viewState.zoom,
      pitch: calculatePitchFromZoom(event.viewState.zoom),
    });
  };

  useEffect(() => {
    if (!selectedLand || !landHasMarket(selectedLand)) return;

    const selectedLandVisible = !landHasMarket(selectedLand)
      ? false
      : visibleLands.some(land => land.tokenSymbol === (selectedLand as Land).tokenSymbol);
    if (
      previousZoomLevelForLandVisibility !== zoomLevelForLandVisibility &&
      zoomLevelForLandVisibility > 0 &&
      !selectedLandVisible
    ) {
      close();
    }
  }, [selectedLand, visibleLands, zoomLevelForLandVisibility, previousZoomLevelForLandVisibility, close]);

  const [isLoaded, setIsLoaded] = useState(false);
  const onLoad = () => {
    setIsLoaded(true);
  };
  useEffect(() => {
    if (!isLoaded || !selectedLand) return;

    const b = selectedLand.mapInformation.boundaries;
    const bounds = [b.southWest.lng, b.southWest.lat, b.northEast.lng, b.northEast.lat] as [
      number,
      number,
      number,
      number,
    ];
    try {
      mapRef.current?.fitBounds(bounds, {animate});
    } catch {}
  }, [isLoaded, animate, selectedLand]);

  return (
    <Map
      mapboxAccessToken={environment.mapboxAccessToken}
      ref={mapRef}
      longitude={state.longitude}
      latitude={state.latitude}
      zoom={state.zoom}
      pitch={state.pitch}
      minZoom={MIN_ZOOM}
      maxZoom={MAX_ZOOM}
      minPitch={MIN_PITCH}
      maxPitch={MAX_PITCH}
      padding={{top: 20, right: mapCenterOffsetX ? mapCenterOffsetX : 20, bottom: 20, left: 20}}
      dragRotate={false}
      touchZoomRotate={false}
      keyboard={false}
      antialias={true}
      mapStyle='mapbox://styles/martingagnon/clkv9g1o2006101qh9k1uhr9w'
      maxBounds={[
        [-124.848974, 24.396308],
        [-66.885444, 49.384358],
      ]}
      interactiveLayerIds={['land-click-trap-nh', 'land-click-trap-city']}
      onZoom={onViewStateChange}
      onMove={onViewStateChange}
      onClick={onMapClick}
      onZoomStart={zoomStartEvent => {
        if (!zoomStartEvent.originalEvent) {
          setIsAutomaticTransition(true);
        }
      }}
      onZoomEnd={zoomEndEvent => {
        setIsAutomaticTransition(false);
        setZoomLevelForLandVisibility(zoomEndEvent.viewState.zoom);
      }}
      onLoad={onLoad}
    >
      <Source id='lands-neighborhood' type='vector' url='mapbox://martingagnon.2zvdfqi2'>
        <Layer
          id='land-highlight-nh'
          type='line'
          source='lands'
          source-layer='mapboxlayer-1igibe'
          paint={{
            'line-color': 'rgba(0, 84, 254, 1)',
            'line-width': 3,
          }}
          filter={landFilter}
        />

        <Layer
          id='land-borders-nh'
          type='fill'
          source='lands'
          source-layer='mapboxlayer-1igibe'
          paint={{'fill-color': 'rgba(43, 84, 254, 0.1)'}}
          filter={mapSelectedLandFilter}
        />

        <Layer
          id='land-click-trap-nh'
          type='fill'
          source='lands'
          source-layer='mapboxlayer-1igibe'
          paint={{'fill-color': 'transparent'}}
        />
      </Source>

      <Source id='lands-cities' type='vector' url='mapbox://martingagnon.9powsefu'>
        <Layer
          id='land-highlight-city'
          type='line'
          source='lands'
          source-layer='mapboxlayer-3hhwjc'
          paint={{
            'line-color': 'rgba(0, 84, 254, 1)',
            'line-width': 3,
          }}
          filter={mapSelectedLandFilter}
        />

        <Layer
          id='land-borders-city'
          type='fill'
          source='lands'
          source-layer='mapboxlayer-3hhwjc'
          paint={{'fill-color': 'rgba(43, 84, 254, 0.1)'}}
          filter={landFilter}
        />

        <Layer
          id='land-click-trap-city'
          type='fill'
          source='lands'
          source-layer='mapboxlayer-3hhwjc'
          paint={{'fill-color': 'transparent'}}
        />
      </Source>

      {showNavigation && <NavigationControl position='bottom-left' showCompass={false} />}
      {visibleLands.map(land => {
        return (
          <Marker
            latitude={land.mapInformation.center.lat}
            longitude={land.mapInformation.center.lng}
            key={land.tokenSymbol}
            onClick={() => onOpenLand(land)}
            anchor='bottom'
          >
            {!displayOnlySelectedLand ? (
              <div className='flex flex-col justify-center'>
                <div className='flex justify-center grow-0 shrink-0'>
                  <img src={pin} alt='' />
                </div>

                <div className='flex items-center py-1 px-2 mt-2 bg-white rounded-full text-xs text-black font-bold'>
                  <LandIcon land={land as Land} />
                  <span className='mx-1'>{t('pages.map.mapTile.title', {landName: land.name})}</span>
                  <span
                    className='data-[positivity="positive"]:text-upgreen data-[positivity="negative"]:text-downred'
                    data-positivity={getNumberPositivityStatus(land.syntheticMarketData.marketPrice.daily.ratio)}
                  >
                    {t('pages.map.mapTile.priceRatio', {
                      value: land.syntheticMarketData.marketPrice.daily.ratio,
                    })}
                  </span>
                </div>
              </div>
            ) : (
              <span></span>
            )}
          </Marker>
        );
      })}
      {visibleUnlistedLands.map(land => {
        return (
          <Marker
            latitude={land.mapInformation.center.lat}
            longitude={land.mapInformation.center.lng}
            key={land.id}
            onClick={event => {
              event.originalEvent.stopPropagation();
              openInformation(land);
            }}
            anchor='bottom'
          >
            <div className='flex flex-col justify-center'>
              <div className='flex justify-center grow-0 shrink-0'>
                <img src={pin} alt='' />
              </div>

              <div className='flex items-center py-1 px-2 mt-2 bg-white rounded-full text-xs text-black font-bold'>
                <LandIcon land={land} />
                <span className='mx-1'>{land.name}</span>
              </div>
            </div>
          </Marker>
        );
      })}
    </Map>
  );
};

export default LandMap;
